import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth, useToast } from "../components/Navbar";

// ── Icon helper ───────────────────────────────────────────────
const Icon = ({ d, size = 18, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    <path d={d} />
  </svg>
);
const ICONS = {
  chevron:  "M9 18l6-6-6-6",
  check:    "M20 6L9 17l-5-5",
  x:        "M18 6L6 18M6 6l12 12",
  clock:    "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  truck:    "M1 3h15v13H1zM16 8h4l3 3v5h-7V8zM5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
  return:   "M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6M3 10l6-6",
  mail:     "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
  shield:   "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  tag:      "M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01",
  alert:    "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  package:  "M12 2l9 4.9V17L12 22 3 17V6.9L12 2zM12 22V12M3 7l9 5 9-5",
  orders:   "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2",
  info:     "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8h.01M12 12v4",
};

// ── Return policy data by category ───────────────────────────
const POLICY_CATEGORIES = [
  {
    id: "clothing",
    emoji: "👗",
    title: "Clothing & Apparel",
    subtitle: "Indian Traditional, Summer Wear, Winter Wear",
    window: "30 days",
    windowColor: "text-emerald-600 bg-emerald-50",
    eligible: [
      "Item received in wrong size or color",
      "Manufacturing defect or damaged on arrival",
      "Item significantly different from description",
      "Item not as shown in product photos",
    ],
    notEligible: [
      "Worn, washed, or altered items",
      "Items without original tags attached",
      "Innerwear, lingerie, or swimwear (hygiene)",
      "Sale or clearance items marked Final Sale",
      "Items returned after 30 days of delivery",
    ],
    condition: "Items must be unworn, unwashed, with all original tags attached and in original packaging.",
    note: null,
  },
  {
    id: "jewelry",
    emoji: "💍",
    title: "Jewelry & Accessories",
    subtitle: "Necklaces, earrings, bracelets, rings",
    window: "30 days",
    windowColor: "text-amber-600 bg-amber-50",
    eligible: [
      "Item received damaged or broken",
      "Wrong item sent",
      "Tarnishing or discoloration within 48 hours of receipt",
      "Significant size difference from listing",
    ],
    notEligible: [
      "Worn jewelry (hygiene and safety reasons)",
      "Customized or personalized pieces",
      "Items without original box/pouch",
      "Tarnishing due to contact with water or perfume after use",
      "Sale items marked Final Sale",
    ],
    condition: "Jewelry must be unworn and returned in original packaging with all certificates or cards included.",
    note: "⚠️ Jewelry is inspected carefully before dispatch. Please photograph any damage immediately upon opening.",
  },
  // {
  //   id: "cosmetics",
  //   emoji: "💄",
  //   title: "Cosmetics & Beauty",
  //   subtitle: "Makeup, skincare, fragrances",
  //   window: "48 hours",
  //   windowColor: "text-rose-600 bg-rose-50",
  //   eligible: [
  //     "Sealed product received damaged or leaking",
  //     "Wrong product sent",
  //     "Product expired or near expiry on arrival",
  //     "Allergic reaction with medical documentation",
  //   ],
  //   notEligible: [
  //     "Opened or used cosmetics (health & safety)",
  //     "Fragrances once opened",
  //     "Products without original seal",
  //     "Change of mind after opening",
  //     "Reaction without medical documentation",
  //   ],
  //   condition: "Cosmetics must be completely sealed and unopened. We cannot accept returns on opened beauty products for health and safety reasons.",
  //   note: "⚠️ Due to hygiene regulations, opened cosmetics cannot be returned under any circumstances unless proven defective.",
  // },
  {
    id: "general",
    emoji: "📦",
    title: "General Rules (All Items)",
    subtitle: "Applies to every category",
    window: "30 days",
    windowColor: "text-blue-600 bg-blue-50",
    eligible: [
      "Item never arrived (lost in transit)",
      "Item arrived severely damaged",
      "Completely wrong order received",
      "Item missing parts or accessories",
    ],
    notEligible: [
      "Change of mind after 30 days",
      "Items purchased during Final Sale events",
      "Gift cards or store credits",
      "Items damaged due to customer misuse",
    ],
    condition: "All returns require proof of purchase (order ID). Items must be in original condition unless the return reason is damage.",
    note: null,
  },
];

// ── How it works steps ────────────────────────────────────────
const HOW_IT_WORKS = [
  { step: "01", icon: ICONS.orders,  title: "Go to Your Order",     desc: "Visit Settings → Orders and open the order you want to return." },
  { step: "02", icon: ICONS.return,  title: "Click Request Return",  desc: "Hit the 'Request Return' button and describe the reason for return." },
  { step: "03", icon: ICONS.truck,   title: "We Arrange Pickup",    desc: "Our team reviews and schedules a pickup from your address." },
  { step: "04", icon: ICONS.shield,  title: "Refund Processed",     desc: "Once we receive and inspect the item, refund is initiated in 5–7 business days." },
];

// ── Accordion item ────────────────────────────────────────────
function PolicyAccordion({ cat, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${open ? "border-stone-200 shadow-md" : "border-stone-100 shadow-sm"}`}>
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{cat.emoji}</span>
          <div>
            <p className="font-bold text-stone-900 text-sm">{cat.title}</p>
            <p className="text-xs text-stone-400 mt-0.5">{cat.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cat.windowColor}`}>
            {cat.window} window
          </span>
          <Icon d={ICONS.chevron}
            className={`text-stone-400 transition-transform duration-300 ${open ? "rotate-90" : ""}`}
            size={16}
          />
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-stone-100">

          {/* Condition */}
          <div className="mt-4 bg-blue-50 rounded-xl px-4 py-3 flex items-start gap-2">
            <Icon d={ICONS.info} size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 leading-relaxed">{cat.condition}</p>
          </div>

          {/* Note */}
          {cat.note && (
            <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
              <Icon d={ICONS.alert} size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">{cat.note}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Eligible */}
            <div>
              <div className="flex items-center gap-1.5 mb-2.5">
                <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Icon d={ICONS.check} size={9} className="text-white" />
                </div>
                <p className="text-xs font-bold text-stone-700 uppercase tracking-widest">Eligible for Return</p>
              </div>
              <ul className="space-y-2">
                {cat.eligible.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 mt-1.5" />
                    <span className="text-sm text-stone-600 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not eligible */}
            <div>
              <div className="flex items-center gap-1.5 mb-2.5">
                <div className="w-4 h-4 rounded-full bg-red-400 flex items-center justify-center flex-shrink-0">
                  <Icon d={ICONS.x} size={9} className="text-white" />
                </div>
                <p className="text-xs font-bold text-stone-700 uppercase tracking-widest">Not Eligible</p>
              </div>
              <ul className="space-y-2">
                {cat.notEligible.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                    <span className="text-sm text-stone-500 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Custom return request form ────────────────────────────────
function CustomReturnForm() {
  const { token, isLoggedIn } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [orders,        setOrders]        = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [reason,        setReason]        = useState("");
  const [details,       setDetails]       = useState("");
  const [selectedItems, setSelectedItems] = useState({}); // { [order_item_id]: { checked, qty, return_type } }
  const [sending,       setSending]       = useState(false);
  const [submitted,     setSubmitted]     = useState(false);

  const REASON_OPTIONS = [
    "Wrong item received",
    "Item damaged on arrival",
    "Item not as described",
    "Wrong size / doesn't fit",
    "Manufacturing defect",
    "Missing parts or accessories",
    "Allergic reaction (with documentation)",
    "Other",
  ];

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  // Load only delivered orders within 7-day return window
  useEffect(() => {
    if (!isLoggedIn || !token) { setOrdersLoading(false); return; }
    fetch(`${API}/api/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.orders || [];
        const now = new Date();
        const eligible = list.filter(o => {
          if (o.status !== "delivered") return false;
          if (o.return_status && o.return_status !== "none") return false;
          const deliveredAt = new Date(o.updated_at || o.placed_at);
          const daysSince = (now - deliveredAt) / (1000 * 60 * 60 * 24);
          return daysSince <= 30;
        });
        setOrders(eligible);
      })
      .catch(() => {})
      .finally(() => setOrdersLoading(false));
  }, [isLoggedIn, token]);

  // Reset item selection whenever the chosen order changes
  useEffect(() => {
    setSelectedItems({});
  }, [selectedOrder]);

  const currentOrder = orders.find(o => String(o.id) === String(selectedOrder));
  const currentItems = currentOrder?.items || [];

  function toggleItem(item) {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        next[item.id] = { qty: 1, return_type: "refund" };
      }
      return next;
    });
  }

  function updateItemQty(itemId, qty, maxQty) {
    const clamped = Math.max(1, Math.min(Number(qty) || 1, maxQty));
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], qty: clamped },
    }));
  }

  function updateItemType(itemId, return_type) {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], return_type },
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedOrder)  return addToast("Please select an order", "error");

    const itemIds = Object.keys(selectedItems);
    if (itemIds.length === 0) return addToast("Please select at least one item to return", "error");

    if (!reason)         return addToast("Please select a reason", "error");
    if (!details.trim()) return addToast("Please describe the issue", "error");

    if (!isLoggedIn) {
      addToast("Please log in to submit a return request", "error");
      navigate("/");
      return;
    }

    const items = itemIds.map(id => ({
      order_item_id: Number(id),
      qty: selectedItems[id].qty,
      return_type: selectedItems[id].return_type,
    }));

    setSending(true);
    try {
      const res = await fetch(`${API}/api/orders/${selectedOrder}/return`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: `[${reason}] ${details.trim()}`, items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit return request");
      setSubmitted(true);
      addToast("Return request submitted successfully!");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setSending(false);
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-10 space-y-4">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Icon d={ICONS.check} size={28} className="text-emerald-500" />
        </div>
        <div>
          <p className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Request Submitted!
          </p>
          <p className="text-sm text-stone-500 mt-1">
            We'll review your request and respond within 24–48 hours.
          </p>
        </div>
        <div className="flex gap-3 justify-center pt-2">
          <Link to="/settings" state={{ tab: "orders" }}
            className="px-6 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 transition">
            View My Orders
          </Link>
          <button onClick={() => { setSubmitted(false); setSelectedOrder(""); setReason(""); setDetails(""); setSelectedItems({}); }}
            className="px-6 py-2.5 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50 transition">
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  const inp = "w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition";
  const lbl = "block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1.5";

  // Not logged in state
  if (!isLoggedIn) {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
          <Icon d={ICONS.orders} size={22} className="text-stone-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-700">Sign in to view your eligible orders</p>
          <p className="text-xs text-stone-400 mt-1">Only delivered orders within the 30-day window will appear</p>
        </div>
        <button onClick={() => navigate("/")}
          className="px-6 py-3 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 transition">
          Sign In
        </button>
      </div>
    );
  }

  // Loading state
  if (ordersLoading) {
    return (
      <div className="flex items-center justify-center py-10 gap-2 text-stone-400">
        <div className="w-4 h-4 border-2 border-stone-200 border-t-stone-500 rounded-full animate-spin" />
        <span className="text-sm">Loading your eligible orders...</span>
      </div>
    );
  }

  // No eligible orders
  if (orders.length === 0) {
    return (
      <div className="text-center py-8 space-y-3">
        <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
          <Icon d={ICONS.package} size={22} className="text-stone-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-stone-700">No eligible orders found</p>
          <p className="text-xs text-stone-400 mt-1 max-w-xs mx-auto">
            Only delivered orders within the 30-day return window appear here.
            If you believe this is an error, contact us directly.
          </p>
        </div>
        <a href="mailto:sagarfashion777@gmail.com"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50 transition">
          <Icon d={ICONS.mail} size={14} />
          Email Us
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      {/* Order selector */}
      <div>
        <label className={lbl}>Select Your Order *</label>
        <select
          className={inp}
          value={selectedOrder}
          onChange={e => setSelectedOrder(e.target.value)}
        >
          <option value="">Choose an order...</option>
          {orders.map(o => {
            const date = new Date(o.placed_at || o.created_at).toLocaleDateString("en-US", {
              month: "short", day: "numeric", year: "numeric"
            });
            const deliveredAt = new Date(o.updated_at || o.placed_at);
            const daysLeft = Math.max(0, 30 - Math.floor((new Date() - deliveredAt) / (1000 * 60 * 60 * 24)));
            return (
              <option key={o.id} value={o.id}>
                Order #{o.id} · {date} · ${Number(o.total || 0).toFixed(2)} · {daysLeft}d left
              </option>
            );
          })}
        </select>
        <p className="text-[11px] text-stone-400 mt-1">
          Showing {orders.length} delivered order{orders.length !== 1 ? "s" : ""} within the 30-day return window
        </p>
      </div>

      {/* Item selector — only shows once an order is chosen */}
      {selectedOrder && (
        <div>
          <label className={lbl}>Select Item(s) to Return *</label>
          <div className="space-y-2">
            {currentItems.map(item => {
              const sel = selectedItems[item.id];
              return (
                <div key={item.id}
                  className={`rounded-xl border px-4 py-3 transition ${sel ? "border-rose-300 bg-rose-50/40" : "border-stone-200 bg-stone-50"}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!sel}
                      onChange={() => toggleItem(item)}
                      className="w-4 h-4 accent-rose-500"
                    />
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-stone-800 truncate">{item.name}</p>
                      <p className="text-[11px] text-stone-400">
                        Qty purchased: {item.qty}{item.size ? ` · Size ${item.size}` : ""}{item.color ? ` · ${item.color}` : ""}
                      </p>
                    </div>
                  </label>

                  {sel && (
                    <div className="mt-3 pl-7 flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">Qty to return:</span>
                        <input
                          type="number"
                          min={1}
                          max={item.qty}
                          value={sel.qty}
                          onChange={e => updateItemQty(item.id, e.target.value, item.qty)}
                          className="w-16 px-2 py-1 rounded-lg border border-stone-200 text-sm text-center"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">Type:</span>
                        <div className="flex rounded-lg overflow-hidden border border-stone-200">
                          {["refund", "exchange"].map(t => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => updateItemType(item.id, t)}
                              className={`px-3 py-1 text-xs font-semibold capitalize transition ${
                                sel.return_type === t
                                  ? "bg-rose-500 text-white"
                                  : "bg-white text-stone-500 hover:bg-stone-50"
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reason dropdown */}
      <div>
        <label className={lbl}>Reason for Return *</label>
        <select
          className={inp}
          value={reason}
          onChange={e => setReason(e.target.value)}
        >
          <option value="">Select a reason...</option>
          {REASON_OPTIONS.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Details textarea */}
      <div>
        <label className={lbl}>
          Additional Details *
          {reason === "Other" && (
            <span className="ml-1 text-rose-400 normal-case font-normal">— please be specific</span>
          )}
        </label>
        <textarea
          className={inp + " resize-none"}
          rows={4}
          placeholder={
            reason === "Item damaged on arrival"
              ? "Describe the damage. If possible, email photos to sagarfashion777@gmail.com"
              : reason === "Wrong size / doesn't fit"
              ? "e.g. Ordered size M but received size L. The tag shows size L..."
              : "Describe your issue in detail so we can process your return quickly..."
          }
          value={details}
          onChange={e => setDetails(e.target.value)}
        />
        <p className="text-[11px] text-stone-400 mt-1">
          The more detail you provide, the faster we can process your return.
        </p>
      </div>

      {/* Policy reminder */}
      <div className="flex items-start gap-2 bg-stone-50 rounded-xl px-4 py-3">
        <Icon d={ICONS.clock} size={14} className="text-stone-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-stone-500 leading-relaxed">
          Returns must be requested within <strong className="text-stone-700">30 days of delivery</strong> {/* (48 hours for cosmetics) */}.
          Items must be in original, unused condition. Size exchanges are free
        </p>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="w-full py-4 rounded-xl bg-rose-500 hover:bg-rose-600 active:scale-[0.98] text-white font-bold text-sm transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        <Icon d={ICONS.return} size={16} />
        {sending ? "Submitting..." : "Submit Return Request"}
      </button>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function Returns() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen bg-stone-50">
      <Helmet>
        <title>Return Policy — Sagar Lifestyle NY LLC</title>
        <meta name="description" content="Our return and refund policy for clothing and jewelry. 30-day returns on most items." />
          {/* cosmetics removed from meta description */}
      </Helmet>

      {/* Hero */}
      <div className="relative overflow-hidden bg-stone-900 py-16 px-6">
        <div className="absolute inset-0 opacity-15"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1600&q=55')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 to-stone-900/50" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-400 mb-3 block">
            Customer Care
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Return & Refund Policy
          </h1>
          <p className="text-white/60 text-base font-light max-w-lg mx-auto">
            We want you to love what you ordered. If something isn't right, we'll make it right.
          </p>
          {/* Quick stats */}
          <div className="flex items-center justify-center gap-8 mt-8 pt-8 border-t border-white/10">
            {[
              ["30 Days",    "Return Window"],
              ["5–7 Days",  "Refund Timeline"],
              ["Free",      "Return Pickup"],
              ["Free",      "Size Exchange"],
            ].map(([val, label]) => (
              <div key={label} className="text-center">
                <p className="text-xl font-bold text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}>{val}</p>
                <p className="text-[11px] text-white/50 uppercase tracking-widest mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 space-y-10">

        {/* How it works */}
        <div>
          <div className="mb-6">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-rose-500 mb-1 block">Process</span>
            <h2 className="text-2xl font-bold text-stone-900"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              How Returns Work
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((step) => (
              <div key={step.step} className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm text-center">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center mx-auto mb-3">
                  <Icon d={step.icon} size={18} className="text-rose-500" />
                </div>
                <p className="text-[10px] font-bold text-stone-300 tracking-widest mb-1">{step.step}</p>
                <p className="text-xs font-bold text-stone-800 mb-1">{step.title}</p>
                <p className="text-[11px] text-stone-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Policy by category */}
        <div>
          <div className="mb-6">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-rose-500 mb-1 block">Guidelines</span>
            <h2 className="text-2xl font-bold text-stone-900"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Policy by Category
            </h2>
            <p className="text-stone-400 text-sm mt-1">
              Click any category to expand the full return rules.
            </p>
          </div>
          <div className="space-y-3">
            {POLICY_CATEGORIES.map((cat, i) => (
              <PolicyAccordion key={cat.id} cat={cat} defaultOpen={i === 0} />
            ))}
          </div>
        </div>

        {/* Refund info */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Icon d={ICONS.shield} size={16} className="text-emerald-500" />
            </div>
            <h2 className="text-base font-bold text-stone-900"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Refund Information
            </h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "How long does a refund take?",     a: "Refunds are initiated within 5–7 business days of receiving and inspecting your return." },
              { q: "Where is the refund sent?",        a: "Refunds go back to your original payment method (credit/debit card). You'll see it in 5–10 business days depending on your bank." },
              { q: "Can I get store credit instead?",  a: "Yes! If you prefer, we can issue store credit which is applied faster. Contact support after submitting your return." },
              { q: "What if my refund is delayed?",    a: "If you haven't received your refund after 10 business days, contact us at sagarfashion777@gmail.com with your order ID." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 py-3 border-b border-stone-50 last:border-0">
                <span className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 flex-shrink-0 mt-0.5 text-xs font-bold">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-stone-800">{item.q}</p>
                  <p className="text-xs text-stone-500 mt-1 leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom return request form */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
          <div className="mb-5">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-rose-500 mb-1 block">
              Submit a Request
            </span>
            <h2 className="text-2xl font-bold text-stone-900"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Request a Return
            </h2>
            <p className="text-stone-400 text-sm mt-1">
              Fill out the form below or go to{" "}
              <Link to="/settings" state={{ tab: "orders" }}
                className="text-rose-500 font-semibold hover:underline">
                Settings → Orders
              </Link>{" "}
              and click the return button on your order.
            </p>
          </div>
          <CustomReturnForm />
        </div>

        {/* Still need help */}
        <div className="text-center bg-stone-900 rounded-2xl px-6 py-8">
          <p className="text-white/60 text-xs uppercase tracking-widest mb-2">Still have questions?</p>
          <h3 className="text-white text-xl font-bold mb-1"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            We're here to help
          </h3>
          <p className="text-white/50 text-sm mb-6">
            Our team responds within 24 hours, Monday to Saturday.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="mailto:sagarfashion777@gmail.com"
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-stone-900 font-semibold text-sm hover:bg-stone-100 transition">
              <Icon d={ICONS.mail} size={15} />
              sagarfashion777@gmail.com
            </a>
            <Link to="/contact"
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-white font-semibold text-sm hover:bg-white/10 transition">
              Visit Contact Page
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}